/**
 * Utilitários para manipulação de imagens
 */

/**
 * Limpa cookies antigos que podem estar causando headers muito grandes
 * @param maxCookieAge - Idade máxima dos cookies em dias (padrão: 30)
 */
export const cleanupOldCookies = (maxCookieAge: number = 30): void => {
  const cookies = document.cookie.split(";");
  const now = new Date();
  const maxAge = maxCookieAge * 24 * 60 * 60 * 1000; // Converter para milissegundos

  cookies.forEach((cookie) => {
    const [name, value] = cookie.trim().split("=");
    if (name && value) {
      // Tentar extrair data de criação do cookie (se disponível)
      // Como não temos acesso direto à data de criação, vamos limpar cookies muito longos
      if (value.length > 1000) {
        // Cookies muito longos podem ser problemáticos
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    }
  });
};

/**
 * Converte um arquivo para base64
 * @param file - Arquivo a ser convertido
 * @returns Promise<string> - String base64 da imagem
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Valida se o arquivo é uma imagem válida
 * @param file - Arquivo a ser validado
 * @returns boolean - True se for uma imagem válida
 */
export const isValidImageFile = (file: File): boolean => {
  const validTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  return validTypes.includes(file.type);
};

/**
 * Valida o tamanho do arquivo
 * @param file - Arquivo a ser validado
 * @param maxSizeInMB - Tamanho máximo em MB (padrão: 5MB)
 * @returns boolean - True se o tamanho for válido
 */
export const isValidFileSize = (
  file: File,
  maxSizeInMB: number = 5,
): boolean => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Verifica se uma imagem tem transparência
 * @param imageData - Dados da imagem do canvas
 * @returns boolean - True se a imagem tem pixels transparentes
 */
export const checkImageTransparency = (imageData: ImageData): boolean => {
  const data = imageData.data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true; // Pixel transparente encontrado
    }
  }
  return false; // Nenhum pixel transparente
};

/**
 * Extrai apenas a parte base64 da string data URL
 * @param dataUrl - String data URL (ex: "data:image/jpeg;base64,/9j/4AAQ...")
 * @returns string - Apenas a parte base64
 */
export const extractBase64FromDataUrl = (dataUrl: string): string => {
  const base64Index = dataUrl.indexOf(",");
  return base64Index !== -1 ? dataUrl.substring(base64Index + 1) : dataUrl;
};

/**
 * Converte uma URL de imagem em base64 usando fetch (melhor para CORS)
 * @param imageUrl - URL da imagem
 * @returns Promise<string> - String base64 da imagem
 */
export const urlToBase64WithFetch = async (
  imageUrl: string,
): Promise<string> => {
  try {
    const response = await fetch(imageUrl, {
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    // Create image from blob and apply white background
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Não foi possível criar contexto do canvas"));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image first. Try to inspect pixels to detect transparency.
        ctx.drawImage(img, 0, 0);

        let hasTransparency = false;
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          hasTransparency = checkImageTransparency(imageData);
        } catch (err) {
          // Não foi possível acessar os pixels (possível CORS).
          // Seguimos sem detecção de transparência; se o canvas estiver tainted,
          // a chamada a toDataURL abaixo pode lançar — trataremos isso.
          hasTransparency = false;
        }

        // Clear canvas and redraw with white background if transparent
        if (hasTransparency) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        }

        try {
          // Usar qualidade máxima para consistência (1.0 = sem perdas)
          const dataUrl = canvas.toDataURL("image/jpeg", 1.0);
          const base64 = extractBase64FromDataUrl(dataUrl);
          resolve(base64);
        } catch (error) {
          // Provavelmente SecurityError por canvas tainted (CORS). Informar claramente.
          reject(
            new Error(
              "Não foi possível converter a imagem para base64: imagem cross-origin sem permissões CORS. Configure Access-Control-Allow-Origin ou use um proxy no servidor.",
            ),
          );
        }
      };

      img.onerror = () => {
        reject(new Error("Erro ao carregar imagem do blob"));
      };

      img.src = URL.createObjectURL(blob);
    });
  } catch (error) {
    throw new Error(`Erro ao buscar imagem: ${error}`);
  }
};

/**
 * Converte uma URL de imagem em base64 (sempre busca imagem fresca)
 * @param imageUrl - URL da imagem
 * @returns Promise<string> - String base64 da imagem
 */
export const urlToBase64 = async (imageUrl: string): Promise<string> => {
  try {
    // Tentar primeiro com fetch (melhor para CORS)
    return await urlToBase64WithFetch(imageUrl);
  } catch (fetchError) {
    // Fallback para Image element
    return new Promise((resolve, reject) => {
      const img = new Image();

      // Configurar timeout para evitar travamentos
      const timeout = setTimeout(() => {
        reject(new Error("Timeout ao carregar imagem"));
      }, 30000); // 30 segundos

      // Tentar primeiro com CORS
      img.crossOrigin = "anonymous";

      img.onload = () => {
        clearTimeout(timeout);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Não foi possível criar contexto do canvas"));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image first to check for transparency
        ctx.drawImage(img, 0, 0);

        // Check if image has transparency
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasTransparency = checkImageTransparency(imageData);

        // Clear canvas and redraw with white background if transparent
        if (hasTransparency) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        }

        try {
          // Usar qualidade máxima para consistência (1.0 = sem perdas)
          const dataUrl = canvas.toDataURL("image/jpeg", 1.0);
          const base64 = extractBase64FromDataUrl(dataUrl);
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = (error) => {
        clearTimeout(timeout);
        // Não tentar draw/read sem CORS — isso geralmente leva a canvas tainted.
        reject(
          new Error(
            "Erro ao carregar imagem com CORS. Se a imagem for externa, habilite Access-Control-Allow-Origin no servidor da imagem ou use um proxy no backend.",
          ),
        );
      };

      img.src = imageUrl;
    });
  }
};

/**
 * Processa um arquivo de imagem e retorna o base64
 * @param file - Arquivo a ser processado
 * @param maxSizeInMB - Tamanho máximo em MB (padrão: 5MB)
 * @returns Promise<string> - String base64 da imagem
 */
export const processImageFile = async (
  file: File,
  maxSizeInMB: number = 5,
): Promise<string> => {
  // Validar tipo do arquivo
  if (!isValidImageFile(file)) {
    throw new Error(
      "Tipo de arquivo não suportado. Use apenas imagens (JPEG, PNG, GIF, WebP).",
    );
  }

  // Validar tamanho do arquivo
  if (!isValidFileSize(file, maxSizeInMB)) {
    throw new Error(
      `Arquivo muito grande. Tamanho máximo permitido: ${maxSizeInMB}MB.`,
    );
  }

  // Converter para base64
  const base64DataUrl = await fileToBase64(file);
  return extractBase64FromDataUrl(base64DataUrl);
};
